import { TreeView } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { filter, isEmpty, join, map, pickBy, size } from 'lodash';
import React from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { isCell, isLogical } from 'components/atoms/ConditionBuilder/utils';
import { AutomaticCondition, AutomaticConditionAttributesTable } from 'interfaces/automaticCondition';
import { MappingFilterMetadata } from 'interfaces/postProcessingAction';
import { uuidv4 } from 'utils/helpers';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { NewCell as NewCellInterface } from '..';
import { CellPopulationGroupState } from './cellPopulationGroupRestore';
import { CellPopulationGroups } from './CellPopulationGroups';
import NewCell from './NewCell';

export interface NewCellsStainProps {
  stain: string;
  newCellsCreated: Record<string, NewCellInterface>;
  addNewCell: (newCellId: string, newCellName: string, newCellValue: AutomaticCondition, stain: string) => void;
  removeNewCell: (newCellId: string) => void;
  onPopulationGroupsChange: (groups: CellPopulationGroupState[]) => void;
  getMappingFiltersMetadataForLogicalQuery: (stain: string) => MappingFilterMetadata[];
}

const NewCellsStain: React.FC<React.PropsWithChildren<NewCellsStainProps>> = ({
  stain,
  newCellsCreated,
  addNewCell,
  removeNewCell,
  onPopulationGroupsChange,
  getMappingFiltersMetadataForLogicalQuery,
}) => {
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();
  const mappingFiltersMetadataForLogicalQuery = getMappingFiltersMetadataForLogicalQuery(stain);
  const mappingFiltersMetadataWithoutArea = filter(
    mappingFiltersMetadataForLogicalQuery,
    (metadata) => metadata.sourceName !== 'area'
  );

  const createNewCell = () => {
    const newCellName = getNewName();
    addNewCell(uuidv4(), newCellName, null, stain);
  };

  const updateNewCell = (newCellId: string, newCellName: string, newCellValue: AutomaticCondition) => {
    const nameByValue = getNewName(newCellValue, newCellName);
    addNewCell(newCellId, nameByValue, newCellValue, stain);
  };

  const getNewName = (newCellValue?: AutomaticCondition, newCellName?: string) => {
    if (newCellValue) {
      return getNameByValue(newCellValue);
    }
    return newCellName ?? `New Cell ${size(newCellsCreated) + 1}`;
  };

  return (
    <Accordion disableGutters expanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {isLoadingStainTypeOptions ? (
          <CircularProgress />
        ) : (
          <Typography component="span" fontWeight={700}>
            {stainTypeIdToDisplayName(stain)}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1} direction="column">
          <Grid item xs={12}>
            <TreeView defaultCollapseIcon={<ExpandMoreIcon />} defaultExpandIcon={<ChevronRightIcon />}>
              {map(
                pickBy(newCellsCreated, (newCell) => !newCell.populationGroup),
                (newCell, newCellId) => {
                  return (
                    <NewCell
                      key={newCellId}
                      cellName={newCell.name}
                      cellValue={newCell.value}
                      mappingFiltersMetadata={mappingFiltersMetadataWithoutArea}
                      removeNewCell={() => removeNewCell(newCellId)}
                      updateNewCell={(newCellName: string, newCellValue: AutomaticCondition) =>
                        updateNewCell(newCellId, newCellName, newCellValue)
                      }
                    />
                  );
                }
              )}
            </TreeView>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1} justifyContent="flex-start">
              <Grid item xs={12}>
                <CellPopulationGroups
                  mappingFiltersMetadata={mappingFiltersMetadataWithoutArea}
                  groups={map(
                    filter(newCellsCreated, (newCell) => Boolean(newCell.populationGroup)),
                    (newCell) => newCell.populationGroup!
                  )}
                  onGroupsChange={onPopulationGroupsChange}
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2">Advanced</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Build a custom logical filter when the population controls do not cover the rule you need.
                </Typography>
                <Button variant="outlined" aria-describedby="simple-popover" onClick={() => createNewCell()}>
                  Add Advanced Cell Filter
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const getNameByValueRecursive = (condition: AutomaticCondition): string => {
  if (isCell(condition)) {
    return condition.displayValue;
  } else if (isLogical(condition)) {
    const cellNames = map(condition.operands, (operand: AutomaticConditionAttributesTable) =>
      getNameByValueRecursive(operand)
    );
    if (!isEmpty(cellNames)) {
      return join(cellNames, ` ${condition.operator} `);
    }
  }

  return '';
};

export const getNameByValue = (newCellValue?: AutomaticCondition) => {
  return getNameByValueRecursive(newCellValue);
};

export default NewCellsStain;
